export enum LoginMethod {
  Password = 'password',
  Totp = 'totp',
  Passkey = 'passkey',
  Social = 'social',
}

export class LoginAttemptExpiredError extends Error {
  constructor() {
    super('The login attempt is no longer active.');
    this.name = 'LoginAttemptExpiredError';
  }
}

export type MfaLoginGrant = {
  consume: () => string;
  assertCurrent: () => void;
  finish: () => void;
  method: LoginMethod;
};

const loginState: { current?: LoginAttempt } = {};

export class LoginAttempt {
  #proof: string | undefined;
  #expiresAt = 0;
  #expiryTimer: ReturnType<typeof setTimeout> | undefined;
  #cancelled = false;
  #consumed = false;

  method: LoginMethod;

  constructor(method: LoginMethod) {
    this.method = method;
    loginState.current?.cancel();
    loginState.current = this;
  }

  assertCurrent = (): void => {
    if (loginState.current !== this || this.#cancelled) {
      throw new LoginAttemptExpiredError();
    }
  };

  cancel(): void {
    this.#cancelled = true;
    this.#proof = undefined;
    clearTimeout(this.#expiryTimer);
  }

  completeDirectPasswordSignIn(): void {
    this.assertCurrent();
    this.cancel();
    if (loginState.current === this) {
      loginState.current = undefined;
    }
  }

  holdProof(proof: string, expiresAt: number): void {
    this.assertCurrent();
    this.#proof = proof;
    this.#expiresAt = expiresAt;
    this.#expiryTimer = setTimeout(() => this.cancel(), expiresAt - Date.now());
  }

  grant(): MfaLoginGrant {
    this.assertCurrent();
    return {
      method: this.method,
      assertCurrent: this.assertCurrent,
      consume: () => {
        this.assertCurrent();
        if (this.#consumed || !this.#proof || this.#expiresAt <= Date.now()) {
          this.cancel();
          throw new LoginAttemptExpiredError();
        }
        const proof = this.#proof;
        this.#proof = undefined;
        this.#consumed = true;
        clearTimeout(this.#expiryTimer);
        return proof;
      },
      finish: () => this.cancel(),
    };
  }

  static assertPasswordExchange(): void {
    if (loginState.current) {
      throw new LoginAttemptExpiredError();
    }
  }
}

export const cancelLoginAttempt = (): void => loginState.current?.cancel();

export const getTotpLoginAttempt = (): LoginAttempt => {
  if (loginState.current?.method !== LoginMethod.Totp) {
    throw new LoginAttemptExpiredError();
  }
  loginState.current.assertCurrent();
  return loginState.current;
};
